import { sleep } from "@/lib/utils";

import type { Api, GeneratedReport, ReportCatalogEntry, ReportHistoryEntry } from "../types";

// Offline stand-in for the analytics reports domain. The catalog mirrors the six
// report types the gateway builds; `generate` fabricates a self-contained HTML
// document (its own <style>) so the viewer, iframe and print-to-PDF flow all work
// without a backend. Generated reports are kept in-memory so the history table
// and re-open (getHtml) behave like the real thing within a session.
const catalog: ReportCatalogEntry[] = [
	{
		type: "platform-sales",
		title: "Сводка продаж площадки",
		description: "Оборот, заказы, средний чек, динамика за период. Без ИИ.",
		scope: "platform",
		ai: false,
		icon: "TrendingUp",
	},
	{
		type: "platform-categories-ai",
		title: "Категории: инсайты и точки роста",
		description: "Продажи по категориям с ИИ-разбором: где растём, где проседаем, что усилить.",
		scope: "platform",
		ai: true,
		icon: "Sparkles",
	},
	{
		type: "platform-sellers-ai",
		title: "Продавцы: рейтинг и рекомендации",
		description: "Топ и аутсайдеры по обороту, возвратам, отзывам. ИИ выделяет проблемных и перспективных.",
		scope: "platform",
		ai: true,
		icon: "Store",
	},
	{
		type: "platform-quality-ai",
		title: "Качество: возвраты и претензии",
		description: "Возвраты по причинам и магазинам, негативные отзывы. ИИ ищет системные проблемы.",
		scope: "platform",
		ai: true,
		icon: "ShieldAlert",
	},
	{
		type: "platform-managers-ai",
		title: "Работа сотрудников: аудит модерации",
		description: "Активность менеджеров, SLA заявок, качество модерации карточек. ИИ выявляет слабые места.",
		scope: "platform",
		ai: true,
		icon: "UserCog",
	},
	{
		type: "platform-finance",
		title: "Финансовая сводка",
		description: "Комиссия, обязательства к выплате, свободные средства. Без ИИ.",
		scope: "platform",
		ai: false,
		icon: "Wallet",
	},
];

const htmlById = new Map<string, string>();
const history: ReportHistoryEntry[] = [];

const mockHtml = (title: string, days: number, aiUsed: boolean): string => `<!doctype html>
<html lang="ru"><head><meta charset="utf-8"><title>${title}</title>
<style>
  :root { --green:#1b4332; --green2:#2d6a4f; --tint:#f6f8f6; --line:#dfe5e1; --ink:#1a2a22; --muted:#5c6b62; }
  * { box-sizing:border-box; }
  body { margin:0; font:14px/1.5 -apple-system,Segoe UI,Roboto,Arial,sans-serif; color:var(--ink); background:#fff; }
  .page { max-width:900px; margin:0 auto; padding:32px; }
  .head { border-bottom:3px solid var(--green); padding-bottom:16px; margin-bottom:24px; }
  h1 { margin:0 0 4px; font-size:22px; color:var(--green); }
  .muted { color:var(--muted); }
  .grid { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; margin:20px 0; }
  .card { background:var(--tint); border:1px solid var(--line); border-radius:10px; padding:16px; }
  .card .n { font-size:24px; font-weight:700; color:var(--green2); }
  .badge { display:inline-block; background:var(--green); color:#fff; border-radius:999px; padding:2px 10px; font-size:12px; }
</style></head>
<body><div class="page">
  <div class="head">
    <h1>${title}</h1>
    <div class="muted">Период: ${days} дн. · демо-данные (офлайн-режим)${aiUsed ? ' · <span class="badge">★ ИИ</span>' : ""}</div>
  </div>
  <div class="grid">
    <div class="card"><div class="muted">Оборот</div><div class="n">2 480 000 руб.</div></div>
    <div class="card"><div class="muted">Заказов</div><div class="n">1 842</div></div>
    <div class="card"><div class="muted">Средний чек</div><div class="n">1 346 руб.</div></div>
  </div>
  <p class="muted">Это заглушка отчёта из офлайн-режима. Подключите API-шлюз, чтобы увидеть реальные данные.</p>
</div></body></html>`;

let counter = 0;

let mockReportModel = "";

export const reports: Api["reports"] = {
	// Офлайн-зеркало настройки модели отчётов.
	async getModel() {
		await sleep(80);
		return mockReportModel;
	},
	async setModel(model: string) {
		await sleep(120);
		mockReportModel = model.trim();
		return mockReportModel;
	},
	async catalog() {
		await sleep(120);
		return catalog.map((entry) => ({ ...entry }));
	},
	async generate(type, days = 30) {
		const entry = catalog.find((c) => c.type === type);
		const aiUsed = entry?.ai ?? false;
		// AI variants take longer on the real backend — mirror the wait so the
		// loader copy ("ИИ анализирует данные…") is exercised offline too.
		await sleep(aiUsed ? 1400 : 350);
		counter += 1;
		const id = `rep_mock_${counter}`;
		const title = entry?.title ?? type;
		const html = mockHtml(title, days, aiUsed);
		htmlById.set(id, html);
		history.unshift({ id, type, title, aiUsed, createdAt: new Date().toISOString() });
		const result: GeneratedReport = { id, title, aiUsed, html };
		return result;
	},
	async history() {
		await sleep(120);
		return history.map((entry) => ({ ...entry }));
	},
	async getHtml(id) {
		await sleep(120);
		return htmlById.get(id) ?? mockHtml("Отчёт", 30, false);
	},
};
